import type { SecurityPolicy } from '../../../../generated/graphql-manager';

import {
  Accordion,
  CopyToClipboard,
  InputDescription,
  TextArea,
  TextInput,
} from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { InfoTooltip } from 'src/components/info-tooltip';

import { useGetWebcastSetupSummaryQuery } from '../../../../generated/graphql-manager';
import { getLanguageKey } from '../../../../utils';
import { generateEmbedCode } from '../../../../utils/generate-embed-code';
import { findReferrerProtectionPolicy } from '../../../../utils/graphql-helpers';
import { useGetWebcastName } from '../../../hooks/use-get-webcast-name';
import { RestrictEmbedding } from '../embedding/restrict-embedding';

import commonStyles from './setup-summary.module.css';

export function SharingInfo() {
  const { webcastId = '' } = useParams();
  const name = useGetWebcastName();
  const { t } = useTranslation();

  const { data } = useGetWebcastSetupSummaryQuery({ variables: { webcastId }, skip: !webcastId });
  const { sharingLink, securityPolicies, additionalLangs, primaryLang } = data?.webcast || {};
  const sharingUrl = sharingLink?.sharingUrl || '';

  const referrerProtectionPolicy = findReferrerProtectionPolicy(securityPolicies);
  const embedCode = generateEmbedCode({ url: sharingUrl, title: name || '' });
  const isMultiLanguages = Boolean(additionalLangs?.length);
  const multipleLanguages = [primaryLang, ...(additionalLangs || [])];

  const getSharingUrlWithLanguage = (language: string) => {
    return `${sharingUrl}/${language}`;
  };

  const getEmbededCodeWithLanguage = (language: string) => {
    return generateEmbedCode({ url: getSharingUrlWithLanguage(language), title: name || '' });
  };

  return (
    <Accordion
      data-testid="sharing-info-accordion"
      buttonContent={
        <div className={commonStyles.accordionLabel}>
          <span>{t('manager.webcastSetup.summary.sharing.title')}</span>

          {referrerProtectionPolicy?.enabled && (
            <span data-testid="embed-restrictions">{t('manager.webcastSetup.summary.sharing.embedRestrictions')}</span>
          )}
        </div>
      }
    >
      {!isMultiLanguages && (
        <>
          <InputDescription inputId="sharing-url" label={t('manager.webcastSetup.summary.sharing.sharingLinkLabel')}>
            <TextInput readOnly value={sharingUrl} data-testid="sharing-url">
              <CopyToClipboard value={sharingUrl} notificationPosition="left" />
            </TextInput>
          </InputDescription>

          <InputDescription inputId="embed-code" label={t('manager.webcastSetup.summary.sharing.embedCodeLabel')}>
            <TextArea readOnly value={embedCode} data-testid="embed-code" rows={3}>
              <CopyToClipboard value={embedCode} notificationPosition="left" />
            </TextArea>
          </InputDescription>
        </>
      )}

      {isMultiLanguages && (
        <>
          <div className={commonStyles.tooltipWrapper}>
            {t('manager.webcastSetup.summary.sharing.sharingLinkMultilanguage')}

            <InfoTooltip label={t('manager.webcastSetup.summary.sharing.sharingLinkTooltip')} position="above" />
          </div>

          {multipleLanguages?.map((language) => {
            if (!language) return null;

            return (
              <InputDescription
                key={language.code}
                inputId={`sharing-url-${language.code}`}
                label={
                  language.__typename === 'CustomLanguage'
                    ? language.name
                    : t(`common.languages.${getLanguageKey(language.code)}`)
                }
              >
                <TextInput
                  readOnly
                  value={getSharingUrlWithLanguage(language.code)}
                  data-testid={`sharing-url-${language.code}`}
                >
                  <CopyToClipboard value={getSharingUrlWithLanguage(language.code)} notificationPosition="left" />
                </TextInput>
              </InputDescription>
            );
          })}

          <div className={commonStyles.tooltipWrapper}>
            {t('manager.webcastSetup.summary.sharing.embedCodeLabel')}

            <InfoTooltip label={t('manager.webcastSetup.summary.sharing.embedCodeTooltip')} position="above" />
          </div>

          {multipleLanguages?.map((language) => {
            if (!language) return null;

            return (
              <InputDescription
                key={language.code}
                inputId={`embed-code-${language.code}`}
                label={
                  language.__typename === 'CustomLanguage'
                    ? language.name
                    : t(`common.languages.${getLanguageKey(language.code)}`)
                }
              >
                <TextArea
                  readOnly
                  value={getEmbededCodeWithLanguage(language.code)}
                  data-testid={`embed-code-${language.code}`}
                  rows={3}
                >
                  <CopyToClipboard value={getEmbededCodeWithLanguage(language.code)} notificationPosition="left" />
                </TextArea>
              </InputDescription>
            );
          })}
        </>
      )}

      {referrerProtectionPolicy?.enabled && (
        <InputDescription inputId="embed-restrictions" label={t('manager.webcastSetup.summary.sharing.allowedDomains')}>
          <RestrictEmbedding readOnly webcastId={webcastId} securityPolicies={securityPolicies as SecurityPolicy[]} />
        </InputDescription>
      )}
    </Accordion>
  );
}
